<template>
	<div class="stats-info lok-scrollbar">
		<div class="stats-info__title">{{ translate("ui.stats") }}</div>
		<HorizontalDecorDivider />
		<section>
			<div class="stats-info__row stats-info__row-level">
				<div>
					{{ translate("ui.player_level") }}
					<Tooltip :heading="translate('ui.player_level')">
						<template #element>
							<AppIconButton
								class="stats-info__row-info"
								:type="IconTypes.INFO"
								:size="12"
								:iconSize="9"
							/>
						</template>
						<template #tooltip>
							<span
								class="stats-info__row stats-info__row-level-tooltip"
							>
								{{ translate("ui.player_level_info") }}
							</span>
						</template>
					</Tooltip>
				</div>
				<div class="stats-info__row-value">
					{{ settlementStore.mainLevel }}
				</div>
			</div>
			<div class="stats-info__row" v-for="(stat, i) in stats" :key="i">
				<div>
					{{ stat.title }}
				</div>
				<div class="stats-info__row-value">{{ stat.value }}</div>
			</div>
			<div class="stats-info__row stats-info__row-login">
				<div>
					{{ translate("ui.account_established") }}:<br />
					{{
						replacePlaceholder(
							translate("ui.days_ago"),
							"{number}",
							createdBeforeDays,
						)
					}}
				</div>
				<div class="stats-info__row-value">
					{{ translate("ui.last_login") }}:<br />
					{{ formatDate(userStore.lastLogin) }}
				</div>
			</div>
		</section>
		<section class="stats-info__button">
			<AppTextButton
				class="claim-rewards-btn"
				:type="StatusTypes.DEFAULT"
				:text="translate('ui.copy_to_clipboard')"
				@click="copyStatsToClipboard"
			/>
			<ToastMessage
				class="stats-message"
				:show-message="!!toastMessage"
				:message="toastMessage ?? ''"
			/>
		</section>
	</div>
</template>

<script lang="ts" setup>
import AppIconButton from "@/components/AppIconButton.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import Tooltip from "@/components/Tooltip.vue";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import {
	tilerepoForestFrameNames,
	tilerepoRoadsFrameNames,
} from "@/game/entities/config";
import { settlementNames } from "@/game/entities/config/settlements";
import useTileRepository from "@/hooks/useTileRepository";
import useResourcesStore from "@/store/useResourcesStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import { type SettlementItem } from "@/types/tile";
import { IconTypes, StatusTypes } from "@/types/ui";
import { formatDate, formatNumber, formatPercent } from "@/utils/format";
import { floorNumber } from "@/utils/number";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { computed, markRaw, ref } from "vue";

const tilerepo = markRaw(useTileRepository());

const resourcesStore = useResourcesStore();
const settlementStore = useSettlementsStore();
const upgradesStore = useUpgradesStore();
const userStore = useUserStore();

const castleEntities = Array.from(settlementNames).reduce(
	(
		acc: Array<SettlementCastleBuildingTileEntity>,
		[, { x, y }]: [number, SettlementItem],
	): Array<SettlementCastleBuildingTileEntity> => {
		const index = tilerepo.getTileIndexByTileCoordinates({
			x,
			y,
		});
		const entity = tilerepo.getTileEntityByTileIndex(
			index,
		) as SettlementCastleBuildingTileEntity;
		if (entity?.isRevealed) acc.push(entity);
		return acc;
	},
	new Array<SettlementCastleBuildingTileEntity>(),
);

const townsConquered = castleEntities.reduce(
	(acc: number, entity: SettlementCastleBuildingTileEntity): number =>
		entity.isActive ? acc + 1 : acc,
	0,
);
const townsDiscovered = castleEntities.reduce(
	(acc: number, entity: SettlementCastleBuildingTileEntity): number =>
		entity.isRevealed && !entity.isActive ? acc + 1 : acc,
	0,
);
const totalTownLevels = castleEntities.reduce(
	(acc: number, entity: SettlementCastleBuildingTileEntity): number =>
		entity.isActive ? acc + entity.level : acc,
	0,
);
const exploredForestTiles = tilerepo.getTileCount0(tilerepoForestFrameNames);
const roadsAndBridges = tilerepo.getTileCount2(tilerepoRoadsFrameNames);

const stats = [
	{
		title: translate("ui.towns_conquered"),
		value: formatNumber(townsConquered),
	},
	{
		title: translate("ui.towns_discovered"),
		value: formatNumber(townsDiscovered),
	},
	{
		title: translate("ui.total_town_levels"),
		value: formatNumber(totalTownLevels),
	},
	{
		title: translate("ui.total_workforce"),
		value: formatNumber(resourcesStore.populationIncome),
	},
	{
		title: translate("ui.total_food_production"),
		value: formatNumber(resourcesStore.foodIncome),
	},
	{
		title: translate("ui.tiles_explored"),
		value: `${formatNumber(tilerepo.exploredTileCount)} (${formatPercent(tilerepo.exploredTilePercent, 1)})`,
	},
	{
		title: translate("ui.forests_on_explored_tiles"),
		value: formatNumber(exploredForestTiles),
	},
	{
		title: translate("ui.explored_water_tiles"),
		value: formatNumber(tilerepo.exploredWaterTileCount),
	},
	{
		title: translate("ui.roads_and_bridges"),
		value: formatNumber(roadsAndBridges),
	},
	{
		title: translate("ui.completed_upgrades"),
		value: formatNumber(upgradesStore.ownedUpgradeOnMaxLevelCount),
	},
	// TODO: Spravit spolu s trackovanim aj inych resourcov (gems, ad-skippers, Xx-clickers).
	// {
	// 	title: translate("ui.super_clickers_used"),
	// 	value: 0,
	// },
];

const toastMessage = ref<string | null>(null);

const createdBeforeDays = computed((): number =>
	floorNumber(
		(Date.now() - new Date(userStore.accountEstablished).getTime()) /
			(24 * 60 * 60 * 1_000),
	),
);

const copyStatsToClipboard = async (): Promise<void> => {
	const playerLevel = `${translate("ui.player_level")}: ${settlementStore.mainLevel}\n`;
	const createdBefore = `\n${translate("ui.account_established")}: ${createdBeforeDays.value}\n`;
	const lastLogin = `${translate("ui.last_login")}: ${new Date(userStore.lastLogin).toLocaleDateString()}`;

	const statsText = stats
		.map(stat => `${stat.title}: ${stat.value}`)
		.join("\n");

	await navigator.clipboard.writeText(
		playerLevel + statsText + createdBefore + lastLogin,
	);
	handleMessage("copied_to_clipboard");
};

const handleMessage = (message: string): void => {
	toastMessage.value = message;
	setTimeout((): void => {
		toastMessage.value = null;
	}, 1_000);
};
</script>

<style lang="scss" scoped>
.stats-info {
	width: 100%;
	@include flex-col-start;
	position: relative;
	gap: 0.5rem;
	padding: 1.25rem;
	padding-top: 0;
	& > section {
		width: 100%;
	}
	&__title {
		padding-top: 0.5rem;
		width: 100%;
		color: #f4d278;
		font-weight: bold;
		font-size: 1.25rem;
		line-height: 1;
		text-align: center;
		text-transform: uppercase;
	}
	&__row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 1rem;
		margin-bottom: 0.5rem;
		padding-bottom: 0.5rem;
		width: 100%;
		font-size: 0.906rem;
		&:not(:last-child) {
			border-bottom: 0.063rem solid #4b4b50;
		}
		&-info {
			height: 0.75rem;
		}

		& > div {
			display: flex;
			align-items: center;
			gap: 0.25rem;
			color: #fff3dd;
		}
		&-level {
			& > div {
				font-weight: bold;
				text-transform: uppercase;
			}
			&-tooltip {
				text-align: center;
			}
		}
		&-login {
			& > div {
				color: #f4d278;
			}
		}
		&-value {
			text-align: right;
		}
	}
	&__button {
		position: relative;
		@include flex-row-center;
	}
}
.stats-message {
	position: absolute;
	top: -2rem;
}

:deep(.horizontal-decor-divider) {
	width: 100%;
}
</style>
